import type { ReactNode } from 'react';

export interface FieldProps {
  /** Uppercase micro-label rendered above the value. */
  readonly label: string;
  /** Main field value (text, mono number, etc.). */
  readonly children: ReactNode;
  /** Optional helper text below the value. */
  readonly hint?: ReactNode;
  /** Visual focused state (parent component manages focus). */
  readonly focused?: boolean;
  readonly className?: string;
}

/**
 * Labeled field card used in Send / Receive / Onboarding screens.
 *
 * Surface-2 background by default. When `focused` is true, the field
 * gets a 2px indigo ring (brand link color) — matches the softer iOS
 * pattern from the design mockups and uses the same accent as
 * interactive nav text.
 *
 * The wrapper carries a `data-field` attribute so the global
 * focus-visible outline (defined in apps/web/src/index.css) is
 * suppressed on children — otherwise the inner <input>/<textarea>
 * would show BOTH its own browser outline AND Field's ring at the
 * same time.
 *
 * The component is purely presentational. The actual <input> or
 * controlled value lives in `children`, so consumers can put any
 * markup inside (mono large number, plain text, placeholder span,
 * complex composition).
 */
export function Field({ label, children, hint, focused = false, className = '' }: FieldProps) {
  return (
    <div
      data-field
      className={[
        'rounded-xl bg-surface-2 px-4 py-3 transition-shadow duration-fast ease-standard',
        focused && 'shadow-[0_0_0_2px_var(--color-link)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="mb-1 text-[9px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
        {label}
      </div>
      <div className="font-mono text-sm font-medium text-text-primary">
        {children}
      </div>
      {hint !== undefined && (
        <div className="mt-1 text-[11px] text-text-tertiary">{hint}</div>
      )}
    </div>
  );
}
